import React, { useMemo } from 'react';
import { useEnvironment } from '../contexts/EnvironmentContext';

export type UserRole = 'admin' | 'member';

type CrudOperations = {
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
};

type SubjectPermissions = {
  [subject: string]: CrudOperations;
};

export enum Subject {
  DASHBOARD = 'dashboard',
  USERS = 'users',
  SETTINGS = 'settings',
  REPORTS = 'reports',
  AGENTS = 'agents',
  CALLS = 'calls',
  MESSAGES = 'messages',
  PROMPTS = 'prompts',
  NUMBERS = 'numbers',
  DNC = 'dnc',
  VOICES = 'voices',
  PROFILE = 'profile',
  PROVIDERS = 'providers',
  ACTIONS = 'actions',
  WEBHOOKS = 'webhooks',
  CAMPAIGNS = 'campaigns',
  BILLING = 'billing',
  ENVIRONMENTS = 'environments',
  ORGANIZATIONS = 'organizations',
  KNOWLEDGE_BASES = 'knowledge-bases',
  INSIGHTS = 'insights',
}

const SUBJECT_LIST = [...Object.values(Subject)];

const memberPermissions: SubjectPermissions = {
  [Subject.DASHBOARD]: { read: true } as CrudOperations,
  [Subject.CALLS]: { create: false, read: true, update: false, delete: false },
  [Subject.AGENTS]: { create: false, read: true, update: false, delete: false },
  [Subject.CAMPAIGNS]: {
    create: false,
    read: true,
    update: false,
    delete: false,
  },
  [Subject.PROMPTS]: {
    create: false,
    read: true,
    update: false,
    delete: false,
  },
  [Subject.VOICES]: { create: false, read: true, update: false, delete: false },
  [Subject.NUMBERS]: {
    create: false,
    read: true,
    update: false,
    delete: false,
  },
  [Subject.DNC]: {
    create: false,
    read: true,
    update: false,
    delete: false,
  },
  [Subject.PROVIDERS]: {
    create: false,
    read: true,
    update: false,
    delete: false,
  },
  [Subject.ACTIONS]: {
    create: false,
    read: true,
    update: false,
    delete: false,
  },
  [Subject.WEBHOOKS]: {
    create: false,
    read: true,
    update: false,
    delete: false,
  },
  [Subject.BILLING]: {
    create: false,
    read: true,
    update: false,
    delete: false,
  },
  // settings page permissions
  [Subject.SETTINGS]: {
    create: false,
    read: true,
    update: false,
    delete: false,
  },
  [Subject.ENVIRONMENTS]: {
    create: false,
    read: true,
    update: false,
    delete: false,
  },
  [Subject.USERS]: { create: false, read: true, update: false, delete: false },
  [Subject.ORGANIZATIONS]: {
    create: false,
    read: true,
    update: false,
    delete: false,
  },
  [Subject.PROFILE]: { read: true } as CrudOperations,
};

export function usePermissions(role: UserRole): SubjectPermissions {
  role = role?.toLowerCase() as UserRole | 'member';

  return useMemo(() => {
    if (role === 'admin') {
      const allAccess: CrudOperations = {
        create: true,
        read: true,
        update: true,
        delete: true,
      };

      const allPermissions: SubjectPermissions = {};
      SUBJECT_LIST.forEach((subject) => {
        allPermissions[subject] = allAccess;
      });

      return allPermissions;
    }

    return memberPermissions;
  }, [role]);
}

type CanProps = {
  operation: keyof CrudOperations;
  subject: string;
  children: React.ReactNode;
};

export function Can({ operation, subject, children }: CanProps) {
  const { role } = useEnvironment();

  const permissions = usePermissions(role);

  const hasPermission = permissions[subject]?.[operation] ?? false;

  if (!hasPermission) {
    return null;
  }

  return <>{children}</>;
}

export function can(
  role: UserRole,
  operation: keyof CrudOperations,
  subject: string,
): boolean {
  role = role?.toLowerCase() as UserRole | 'member';

  if (role === 'admin') {
    return true;
  }

  return memberPermissions[subject]?.[operation] ?? false;
}
